import React, { useEffect, useState } from "react";
import { FiEye, FiEyeOff, FiSearch } from "react-icons/fi";
import { LuPencil, LuPlusCircle } from "react-icons/lu";
import { Pagination } from "@mui/material";
import {
  Button,
  Card,
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Typography,
} from "@material-tailwind/react";
import { IoSettingsOutline } from "react-icons/io5";
import { FaRegSquareCheck } from "react-icons/fa6";
import { MdOutlineMoreHoriz, MdPassword } from "react-icons/md";
import DeleteIcon from "../../assets/CloseOrDelete.svg";
import AddAdmin from "../../modal/AddAdmin";
import { useNavigate, useParams } from "react-router-dom";
import { mailPattern, phoneNumber, strongPwd } from "../../variables/constants";
import toast from "react-hot-toast";
import {
  deleteServices,
  getServices,
  postServices,
  putServices,
} from "../../apiServices/apiServices";
import LoadingGif from "../../assets/loading_anim.gif";
import NoDataFound from "../../components/NoDataFound";
import ChangePasswordModal from "../../modal/ChangePasswordModal";

const TABLE_HEAD = [
  "Name",
  "Phone",
  "Email",
  "Password",
  "Registered On",
  <IoSettingsOutline className="text-3xl" />,
];

function ManageAdmins() {
  const [isModalOpen, setIsModalOpen] = useState({
    add_admin: false,
    update_admin: false,
    change_password: false,
  });
  const [fieldData, setFieldData] = useState({
    full_name: "",
    phone: "",
    email: "",
    password: "",
  });
  const [changeFieldData, setChangeFieldData] = useState({
    role: "",
    admin_id: "",
    new_password: "",
    confirm_password: "",
  });
  const [selectedRoleOption, setSelectedRoleOption] = useState(null);
  const [isValid, setIsValid] = useState({
    phone: true,
    email: true,
    password: true,
  });
  const [showPassword, setShowPassword] = useState({});
  const [loading, setLoading] = useState({ screen: false, btn: false });
  const [adminList, setAdminList] = useState([]);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);
  const access_token = localStorage.getItem("gassol-super-admin-token");
  const { page_nmr2 } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchAdminsData();
  }, [page_nmr2]);

  const fetchAdminsData = async () => {
    setLoading({ ...loading, screen: true });
    try {
      const res = await getServices(
        `/api/manage/admin/list?page=${page_nmr2}&limit=5&role=2`,
        access_token
      );
      setLoading({ ...loading, screen: false });

      if (res.responseCode === 200) {
        setAdminList(res.responseData);
      } else {
        setAdminList([]);
      }
    } catch (error) {
      setLoading({ ...loading, screen: false });
      toast.error(error);
    }
  };

  const handleopenAddModal = () => {
    setIsModalOpen({ ...isModalOpen, add_admin: true });
    setIsPasswordVisible(false);
    setFieldData({
      ...fieldData,
      full_name: "",
      phone: "",
      email: "",
      password: "",
    });
  };

  const handleOpenUpdateModal = (admin_data) => {
    setIsModalOpen({ ...isModalOpen, update_admin: true });
    setIsPasswordVisible(false);
    setFieldData({
      ...fieldData,
      full_name: admin_data.name,
      phone: admin_data.phone?.substring(3),
      email: admin_data.email,
      admin_id: admin_data._id,
    });
  };

  const handleOpenChangePasswordModal = (admin_data) => {
    setIsModalOpen({ ...isModalOpen, change_password: true });
    setIsPasswordVisible(false);
    setChangeFieldData({
      ...changeFieldData,
      admin_id: admin_data._id,
      role: admin_data.role,
      new_password: "",
      confirm_password: "",
    });
  };

  const handleChangePasswordChange = (e) => {
    if (e.target.name === "new_password") {
      if (strongPwd.test(e.target.value)) {
        setIsValid({ ...isValid, password: true });
      } else {
        setIsValid({ ...isValid, password: false });
      }
    }
    setChangeFieldData({ ...changeFieldData, [e.target.name]: e.target.value });
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    if (!isValid.password) {
      toast.error(
        "Password must have atleast 1 lowercase, number, special characters and minimum 8 characters."
      );
      setLoading({ ...loading, btn: false });
    } else if (
      changeFieldData.confirm_password !== changeFieldData.new_password
    ) {
      toast.error("New password and confirm password should match.");
    } else {
      try {
        const res = await postServices(
          `/api/change/password`,
          {
            new_pass: changeFieldData.new_password,
            cnfm_pass: changeFieldData.confirm_password,
            id: changeFieldData.admin_id,
            role: changeFieldData.role,
          },
          access_token
        );

        if (res.responseCode === 200) {
          fetchAdminsData();
          setIsModalOpen({ ...isModalOpen, change_password: false });
          toast.success("Password changed.");
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        toast.error(error);
      }
    }
  };

  const handlePaginationChange = (e, page) => {
    navigate(`/manage_admins/${page}`);
  };

  const handleChange = (e) => {
    if (e.target.name === "phone") {
      if (phoneNumber.test(e.target.value)) {
        setIsValid({ ...isValid, phone: true });
      } else {
        setIsValid({ ...isValid, phone: false });
      }
    } else if (e.target.name === "email") {
      if (mailPattern.test(e.target.value)) {
        setIsValid({ ...isValid, email: true });
      } else {
        setIsValid({ ...isValid, email: false });
      }
    } else if (e.target.name === "password") {
      if (strongPwd.test(e.target.value)) {
        setIsValid({ ...isValid, password: true });
      } else {
        setIsValid({ ...isValid, password: false });
      }
    }
    setFieldData({ ...fieldData, [e.target.name]: e.target.value });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    if (!isValid.phone) {
      toast.error("Invalid phone number.");
      setLoading({ ...loading, btn: false });
    } else if (!isValid.email) {
      toast.error("Invalid email.");
      setLoading({ ...loading, btn: false });
    } else if (!isValid.password) {
      toast.error(
        "Password must have atleast 1 lowercase, number, special characters and minimum 8 characters."
      );
      setLoading({ ...loading, btn: false });
    } else {
      try {
        const res = await postServices(
          `/api/manage/admin/add`,
          {
            name: fieldData.full_name,
            email: fieldData.email,
            phone: "+91" + fieldData.phone,
            password: fieldData.password,
          },
          access_token
        );
        setLoading({ ...loading, btn: false });
        if (res.responseCode === 200) {
          setIsModalOpen({ ...isModalOpen, add_admin: false });
          toast.success("New Admin added...");
          fetchAdminsData();
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        setLoading({ ...loading, btn: false });
        toast.error(error);
      }
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, btn: true });

    if (!isValid.phone) {
      toast.error("Invalid phone number.");
      setLoading({ ...loading, btn: false });
    } else if (!isValid.email) {
      toast.error("Invalid email.");
      setLoading({ ...loading, btn: false });
    } else {
      try {
        const res = await putServices(
          `/api/manage/admin/edit?admin_id=${fieldData.admin_id}`,
          {
            name: fieldData.full_name,
            email: fieldData.email,
            phone: "+91" + fieldData.phone,
          },
          access_token
        );
        setLoading({ ...loading, btn: false });
        if (res.responseCode === 200) {
          setIsModalOpen({ ...isModalOpen, update_admin: false });
          toast.success("Admin updated...");
          fetchAdminsData();
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        setLoading({ ...loading, btn: false });
        toast.error(error);
      }
    }
  };

  const handleDeleteAdmin = async (admin_data) => {
    if (window.confirm("Do you want to delete this admin?")) {
      try {
        const res = await deleteServices(
          `/api/manage/admin/delete?admin_id=${admin_data._id}`,
          access_token
        );
        if (res.responseCode === 200) {
          toast.success("Admin deleted...");
          if (adminList.data?.length > 1) {
            fetchAdminsData();
          } else {
            navigate(`/manage_admins/${page_nmr2 - 1}`);
            fetchAdminsData();
          }
        } else {
          toast.error(res.responseMessage);
        }
      } catch (error) {
        toast.error(error);
      }
    }
  };

  const handleAdminSearchChange = async (e) => {
    const page = e.target.value?.length > 0 ? 1 : page_nmr2;

    if (e.target.value.length > 0) {
      try {
        const res = await getServices(
          `/api/manage/admin/list?role=2&page=${page}&limit=5&searchkey=${e.target.value}`
        );
        if (res.responseCode === 200) {
          setAdminList(res.responseData);
        } else {
          setAdminList([]);
        }
      } catch (error) {
        toast.error(error);
      }
    } else {
      fetchAdminsData(false);
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };
  const toggleConfirmPasswordVisibility = () => {
    setIsConfirmPasswordVisible(!isConfirmPasswordVisible);
  };

  const toggleAdminPasswordVisibility = (item_id) => {
    setShowPassword((prev) => ({
      ...prev,
      [item_id]: !prev[item_id],
    }));
  };

  return (
    <>
      <div className="p-6">
        <h1 className="text-[#19161F] text-4xl font-bold">Admins</h1>
        <div className="flex flex-col md:flex-row justify-between items-center relative mt-5">
          <div className="relative w-full md:w-auto">
            <input
              type="text"
              className="py-4 pl-12 pr-4 outline-none rounded-xl w-full md:min-w-[280px] xl:min-w-[350px]"
              placeholder="Search by Name"
              style={{
                boxShadow:
                  "0 4px 8px 0 rgba(0, 0, 0, 0), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
              }}
              onChange={handleAdminSearchChange}
            />
            <FiSearch className="absolute top-1/2 transform -translate-y-1/2 left-4 text-[#BDBDBD]" />
          </div>
          <Button
            onClick={handleopenAddModal}
            className="mt-4 md:mt-0 font-poppins bg-[#BD3B24] font-medium text-xs sm:text-sm xl:text-base rounded-xl flex items-center gap-2 px-4 sm:px-6 ml-auto outline-none"
          >
            <LuPlusCircle />
            Add Admin
          </Button>
        </div>

        {!loading.screen ? (
          <div>
            {adminList?.data?.length > 0 ? (
              <Card
                className="h-full w-full rounded-none overflow-x-scroll mt-8"
                style={{
                  boxShadow:
                    "0 4px 8px 0 rgba(0, 0, 0, 0), 0 6px 20px 0 rgba(0, 0, 0, 0.06)",
                }}
              >
                <table className="w-full min-w-max table-auto text-left">
                  <thead>
                    <tr>
                      {TABLE_HEAD.map((head) => (
                        <th
                          key={head}
                          className="border-b border-blue-gray-100 bg-[#03438D] p-4"
                        >
                          <Typography
                            variant="small"
                            color="blue-gray"
                            className="font-poppins text-white font-semibold uppercase"
                          >
                            {head}
                          </Typography>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adminList?.data?.map((admin, index) => {
                      return (
                        <tr key={index}>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold flex items-center gap-2"
                            >
                              <FaRegSquareCheck className="text-lg text-[#BD3B24]" />
                              {admin.name ?? "-"}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {admin.phone ?? "-"}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {admin.email ?? "-"}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold flex items-center gap-2"
                            >
                              {showPassword[admin._id]
                                ? admin.password
                                : "*".repeat(admin.password.length)}
                              {!showPassword[admin._id] ? (
                                <FiEyeOff
                                  onClick={() =>
                                    toggleAdminPasswordVisibility(admin._id)
                                  }
                                  className="cursor-pointer"
                                />
                              ) : (
                                <FiEye
                                  onClick={() =>
                                    toggleAdminPasswordVisibility(admin._id)
                                  }
                                  className="cursor-pointer"
                                />
                              )}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins font-semibold"
                            >
                              {admin.createdAt ?? "-"}
                            </Typography>
                          </td>
                          <td className="p-4">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-poppins text-xl"
                            >
                              <Menu placement="left">
                                <MenuHandler>
                                  <Button className="rounded-full p-2 bg-transparent shadow-none outline-none">
                                    <MdOutlineMoreHoriz className="text-lg text-black" />
                                  </Button>
                                </MenuHandler>
                                <MenuList className="p-0 w-fit min-w-[unset] border-0 shadow-none flex">
                                  <MenuItem
                                    className="w-fit font-poppins rounded-full py-2"
                                    onClick={() => handleDeleteAdmin(admin)}
                                  >
                                    <img src={DeleteIcon} alt="" />
                                  </MenuItem>
                                  <MenuItem
                                    title="Update Details"
                                    className="w-fit font-poppins rounded-full"
                                    onClick={() => handleOpenUpdateModal(admin)}
                                  >
                                    <LuPencil className="font-bold text-lg text-[#03438D]" />
                                  </MenuItem>
                                  <MenuItem
                                    title="Change Password"
                                    className="w-fit font-poppins rounded-full"
                                    onClick={() =>
                                      handleOpenChangePasswordModal(admin)
                                    }
                                  >
                                    <MdPassword className="font-bold text-lg text-[#03438D]" />
                                  </MenuItem>
                                </MenuList>
                              </Menu>
                            </Typography>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            ) : (
              <NoDataFound />
            )}
          </div>
        ) : (
          <div className="w-full flex justify-center items-center h-[60vh]">
            <img src={LoadingGif} alt="" />
          </div>
        )}

        <div className="flex items-center justify-center gap-4 mt-8">
          <div className="flex items-center justify-center gap-2">
            <Pagination
              count={adminList?.totalPages ?? 1}
              defaultPage={Number(page_nmr2) ?? 1}
              siblingCount={0}
              boundaryCount={1}
              onChange={(e, p) => handlePaginationChange(e, p)}
              sx={{
                fontFamily: "Poppins",
                ".MuiPaginationItem-root": {
                  fontFamily: "Poppins",
                  borderRadius: "8px",
                  color: "#BDBDBD",
                },
              }}
            />
          </div>
        </div>
      </div>

      <AddAdmin
        open={isModalOpen.add_admin}
        onClose={() => setIsModalOpen({ ...isModalOpen, add_admin: false })}
        handleChange={handleChange}
        fieldData={fieldData}
        handleAddSubmit={handleAddSubmit}
        loading={loading}
        modal={isModalOpen}
        isPasswordVisible={isPasswordVisible}
        togglePasswordVisibility={togglePasswordVisibility}
        setSelectedRoleOption={setSelectedRoleOption}
        selectedRoleOption={selectedRoleOption}
      />

      <AddAdmin
        open={isModalOpen.update_admin}
        onClose={() => setIsModalOpen({ ...isModalOpen, update_admin: false })}
        handleChange={handleChange}
        fieldData={fieldData}
        loading={loading}
        modal={isModalOpen}
        handleUpdateSubmit={handleUpdateSubmit}
      />

      <ChangePasswordModal
        open={isModalOpen.change_password}
        onClose={() =>
          setIsModalOpen({ ...isModalOpen, change_password: false })
        }
        isPasswordVisible={isPasswordVisible}
        isConfirmPasswordVisible={isConfirmPasswordVisible}
        togglePasswordVisibility={togglePasswordVisibility}
        toggleConfirmPasswordVisibility={toggleConfirmPasswordVisibility}
        handleChangePasswordChange={handleChangePasswordChange}
        handleChangePasswordSubmit={handleChangePasswordSubmit}
      />
    </>
  );
}

export default ManageAdmins;
